import React, { useMemo, useEffect } from 'react';
import styled from 'styled-components';

import { Input } from './input';
import { humanizeEnum } from '../../utils/humanizeEnum';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';
import { colorScheme } from '../../theme';

const StyledLabel = styled.label`
  position: absolute;
  pointer-events: none;
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${colorScheme.input.border};
  left: 24px;
  top: 4px;
  transition: 0.2s ease transform;
  padding: 2px 4px;
  border-radius: 8px;
  transform-origin: left top;

  .hdn-input:focus + &,
  .hdn-input:not(:placeholder-shown) + & {
    transform: translate(-16px, -14px) scale(0.75);
    background: ${colorScheme.input.background};
  }
`;

interface Props<T extends object> {
  id: string;
  label?: string;
  data: T[];
  setFilteredList: React.Dispatch<React.SetStateAction<T[]>>;
  searchBy?: (keyof T)[] | keyof T;
  defaultValue?: string;
}

export function SearchInput<T extends object>({ id, label, data, setFilteredList, searchBy, defaultValue }: Props<T>) {
  const { bind: search } = useDebouncedInput(defaultValue ?? '');

  const searchByArray: (keyof T)[] = useMemo(() => {
    if (Array.isArray(searchBy)) {
      return searchBy;
    }
    if (searchBy) {
      return [searchBy];
    }
    return [];
  }, [searchBy]);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const searchValue = search.value.toLowerCase();

    if (!searchValue) {
      return data;
    }

    return data.filter((item) => {
      if (searchByArray.length > 0) {
        return searchByArray.some((key) => {
          const value = String(item[key] || '').toLowerCase();
          return value.includes(searchValue);
        });
      }
      return Object.values(item as object)
        .join(' ')
        .toLowerCase()
        .includes(searchValue);
    });
  }, [search.value, data, searchByArray]);

  useEffect(() => {
    setFilteredList((prev) => (JSON.stringify(prev) === JSON.stringify(filteredData) ? prev : filteredData));
  }, [filteredData]);

  return (
    <>
      <Input id={id} className="hdn-input hdn-input--transparent-placeholder" placeholder="" {...search} type="search" autoFocus />
      <StyledLabel htmlFor={id}>
        {label ||
          `Search by ${searchByArray.length > 0 ? searchByArray.map((key) => humanizeEnum(String(key), false)).join(', ') : 'any field'}`}
      </StyledLabel>
    </>
  );
}
